import { Amplify } from "aws-amplify";
import { AuthError } from "aws-amplify/auth";
import "moment-duration-format";
import { useEffect, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Route, BrowserRouter as Router, Routes, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import awsConfig from "./amplifyconfiguration.json";
import { fetchUser } from "./common/slices/auth";
import { setActive } from "./common/slices/location";
import { subscribeUserNotification, unsubscribeUserNotifications } from "./common/slices/notification";
import { fetchUser as fetchUserProfile, selectUser as selectUserProfile } from "./common/slices/user";
import theme from "./common/theme";
import AccessDenied from "./components/AccessDenied";
import Login from "./components/Login";
import ModalError from "./components/ModalError";
import NewPassword from "./components/NewPassword";
import ScreenListDailyReport from "./components/ScreenListDailyReport";
import ScreenListSafetyForms from "./components/ScreenListSafetyForms";
import ScreenListTimesheets from "./components/ScreenListTimesheets";
import ScreenListUsers from "./components/ScreenListUsers";
import ScreenManageSafetyForms from "./components/ScreenManageSafetyForms";
import ScreenSettings from "./components/ScreenSettings";
import ScreenImages from "./components/ScreenImages";
import { pathNames } from "./constants";
import { defaultErrorContext, ErrorContext } from "./ErrorContext";

Amplify.configure(awsConfig);

const RouteNotFound = () => "Route Not Found"

function App() {
    return (
        <Router>
            <URApp />
        </Router>
    );
}

const URApp = () => {
    const userProfile = useSelector(selectUserProfile)
    const dispatch = useDispatch();
    const nav = useNavigate();
    const [error, setError] = useState({ errorMessage: null, forSupport: null });

    const onNewNotification = async (notification) => {
        toast.success(notification.subject);
    }

    useEffect(() => {
        (async () => {
            try {
                const user = await dispatch(fetchUser());
                const profile = await dispatch(fetchUserProfile());
                if (!user) {
                    nav("/");
                }
                else if (profile?.role !== 'ADMIN') { nav('/access-denied') }
                dispatch(subscribeUserNotification(onNewNotification))
            } catch (ex) {
                if (!ex instanceof AuthError) {
                    setError({ errorMessage: "Failed to initialize user", forSupport: ex.message })
                }
                nav("/");
            }
        })();

        return () => {
            dispatch(unsubscribeUserNotifications());
            console.log('cleanup whole app');
        }
    }, []);

    useEffect(() => {
        if (userProfile) {
            dispatch(setActive(userProfile.locationId));
        }
    }, [userProfile])

    return (
        <ErrorContext.Provider value={{ ...error, setError }}>
            <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/new-password" element={<NewPassword />} />
                <Route path="/access-denied" element={<AccessDenied />} />
                <Route path={`${pathNames.SAFETYFORMS}/manage/:id?`} element={<ScreenManageSafetyForms />} />
                <Route path={`${pathNames.SAFETYFORMS}/:id?`} element={<ScreenListSafetyForms />} />
                <Route path={`${pathNames.DAILYREPORT}/:id?`} element={<ScreenListDailyReport />} />
                <Route path={`${pathNames.TIMESHEETS}/:id?`} element={<ScreenListTimesheets />} />
                <Route path={`${pathNames.USERS}/:id?`} element={<ScreenListUsers />} />
                <Route path={`${pathNames.IMAGES}/:id?`} element={<ScreenImages />} />
                <Route path={pathNames.SETTINGS} element={<ScreenSettings />} />
                <Route path={'/*'} element={<RouteNotFound />} />
            </Routes>
            {error?.errorMessage && <ModalError errorMessage={error?.errorMessage} forSupport={error?.forSupport} onClose={() => setError(defaultErrorContext)} />}
            <ToastContainer position="bottom-right" toastStyle={{ borderRadius: 0, backgroundColor: theme.palette.light }} bodyStyle={{ color: theme.palette.text }} progressStyle={{ background: theme.palette.text }} icon={<FaCheckCircle />} />
        </ErrorContext.Provider>
    );
};

export default App;
